// Auth validation utilities

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTP_CODE_REGEX = /^\d{6}$/;
const TELEGRAM_LOGIN_REGEX = /^[a-zA-Z0-9_]{5,32}$/;
const WHATSAPP_NUMBER_REGEX = /^[1-9][0-9]{6,14}$/;

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export function validateEmail(email: string): ValidationResult {
  if (!email) {
    return { isValid: false, error: 'errors.emailRequired' };
  }
  if (!EMAIL_REGEX.test(email)) {
    return { isValid: false, error: 'errors.emailInvalid' };
  }
  return { isValid: true };
}

export function validatePassword(password: string): ValidationResult {
  if (!password) {
    return { isValid: false, error: 'errors.passwordRequired' };
  }
  return { isValid: true };
}

export function validatePasswordMatch(
  password: string,
  confirmPassword: string
): ValidationResult {
  if (password !== confirmPassword) {
    return { isValid: false, error: 'errors.passwordsDoNotMatch' };
  }
  return { isValid: true };
}

export function validateOtpCode(code: string): ValidationResult {
  if (!OTP_CODE_REGEX.test(code)) {
    return { isValid: false, error: 'errors.codeInvalid' };
  }
  return { isValid: true };
}

export function validateFullName(fullName: string): ValidationResult {
  if (!fullName || fullName.trim().length === 0) {
    return { isValid: false, error: 'errors.fullNameRequired' };
  }
  return { isValid: true };
}

export function validateCountry(country: string): ValidationResult {
  if (!country) {
    return { isValid: false, error: 'errors.countryRequired' };
  }
  return { isValid: true };
}

export function validateTermsAccepted(accepted: boolean): ValidationResult {
  if (!accepted) {
    return { isValid: false, error: 'errors.termsRequired' };
  }
  return { isValid: true };
}

export function validateUsResidencyConfirmed(confirmed: boolean): ValidationResult {
  if (!confirmed) {
    return { isValid: false, error: 'errors.usResidencyRequired' };
  }
  return { isValid: true };
}

export function normalizeTelegramLogin(value: string): string {
  return value.trim().replace(/^@+/, '');
}

export function normalizeWhatsappNumber(value: string): string {
  return value.replace(/\D/g, '');
}

export function validateTelegram(value: string): ValidationResult {
  const normalized = normalizeTelegramLogin(value);
  if (!normalized) {
    return { isValid: true };
  }
  if (!TELEGRAM_LOGIN_REGEX.test(normalized)) {
    return { isValid: false, error: 'errors.telegramInvalid' };
  }
  return { isValid: true };
}

export function validateWhatsapp(value: string): ValidationResult {
  if (!value.trim()) {
    return { isValid: true };
  }
  const normalized = normalizeWhatsappNumber(value);
  if (!WHATSAPP_NUMBER_REGEX.test(normalized)) {
    return { isValid: false, error: 'errors.whatsappInvalid' };
  }
  return { isValid: true };
}

export interface RegistrationContactFields {
  telegram?: string | null;
  whatsapp?: string | null;
}

export function buildRegistrationContactPayload(
  telegram: string,
  whatsapp: string,
): Pick<RegistrationContactFields, 'telegram' | 'whatsapp'> {
  const normalizedTelegram = normalizeTelegramLogin(telegram);
  const normalizedWhatsapp = normalizeWhatsappNumber(whatsapp);

  return {
    ...(normalizedTelegram ? { telegram: normalizedTelegram } : {}),
    ...(normalizedWhatsapp ? { whatsapp: normalizedWhatsapp } : {}),
  };
}
